import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getJson } from "@/lib/http";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Calculator, Eye, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { Retailer, RateTemplate } from "@shared/schema";
import { LoadingState } from "@/components/ui/loading-state";
import { ErrorBoundary } from "@/components/ErrorBoundary";

type OperatorDiscountRule = Record<string, unknown>;
type InsertOperatorDiscountRule = Record<string, unknown>;

interface DiscountRuleFormData {
  name?: string;
  taskId?: number; // Now required but can be undefined initially
  campaignCountMin: number;
  campaignCountMax?: number;
  storeCountMin: number;
  storeCountMax?: number;
  effectiveRate: number;
  isActive: boolean;
  conditionLogic: "AND" | "OR";
}



function ComprehensiveRemunerationContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const [editingRule, setEditingRule] = useState<any>(null);
  
  const [formData, setFormData] = useState<DiscountRuleFormData>({
    name: "",
    taskId: undefined, // Will be set when task is selected
    campaignCountMin: 1,
    campaignCountMax: undefined,
    storeCountMin: 1,
    storeCountMax: undefined,
    effectiveRate: 15.00,
    isActive: true,
    conditionLogic: "AND"
  });



  // Data queries - optimized for performance
  const { data: tasks = [], isLoading: tasksLoading, error: tasksError } = useQuery<any[]>({
    queryKey: ["/api/tasks"],
    queryFn: async () => {
      const response = await getJson<{ success: boolean; tasks: any[] }>('/api/tasks');
      return response.tasks || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: allRules = [], isLoading: rulesLoading } = useQuery<any[]>({
    queryKey: ["/api/admin/operator-discount-rules-enhanced"],
    queryFn: async () => getJson('/api/admin/operator-discount-rules-enhanced'),
  });

  // Optional queries - only loaded when needed
  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ["/api/retailers"],
    queryFn: async () => getJson('/api/retailers'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false, // Only fetch when explicitly needed
  });

  const { data: rateTemplates = [] } = useQuery<RateTemplate[]>({
    queryKey: ["/api/rate-templates"],
    queryFn: async () => getJson('/api/rate-templates'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false, // Only fetch when explicitly needed
  });

  const { data: mediaFormats = [] } = useQuery<any[]>({
    queryKey: ["/api/media-formats"],
    queryFn: async () => getJson('/api/media-formats'),
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: false, // Only fetch when explicitly needed
  });

  const { data: legacyRules = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/operator-discount-rules"],
    queryFn: async () => getJson('/api/admin/operator-discount-rules'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Only fetch when explicitly needed
  });



  // Mutations
  const createRuleMutation = useMutation({
    mutationFn: async (data: InsertOperatorDiscountRule) => {
      return await apiRequest("/api/admin/operator-discount-rules-enhanced", "POST", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules-enhanced"] });
      toast({ title: "Success", description: "Task rule created successfully" });
      setIsCreateDialogOpen(false);
      resetForm();
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateRuleMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: number; updates: Partial<OperatorDiscountRule> }) => {
      return await apiRequest(`/api/admin/operator-discount-rules-enhanced/${id}`, "PUT", updates);
    },
    onSuccess: () => {
      // Invalidate and refetch the queries to get fresh data
      queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules-enhanced"] });
      queryClient.refetchQueries({ queryKey: ["/api/admin/operator-discount-rules-enhanced"] });
      toast({ title: "Success", description: "Task rule updated successfully" });
      setIsEditDialogOpen(false);
      setEditingRule(null);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteRuleMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/admin/operator-discount-rules-enhanced/${id}`, "DELETE");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules-enhanced"] });
      toast({ title: "Success", description: "Task rule deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deleteLegacyRuleMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest(`/api/admin/operator-discount-rules/${id}`, "DELETE");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules"] });
      toast({ title: "Success", description: "Legacy rule deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });



  const resetForm = () => {
    setFormData({
      name: "",
      taskId: undefined,
      campaignCountMin: 1,
      campaignCountMax: undefined,
      storeCountMin: 1,
      storeCountMax: undefined,
      effectiveRate: 15.00,
      isActive: true,
      conditionLogic: "AND"
    });
  };

  const handleCreateRule = () => {
    if (!formData.taskId) {
      toast({ title: "Error", description: "Please select a task", variant: "destructive" });
      return;
    }



    if (!formData.name?.trim()) {
      toast({ title: "Error", description: "Please enter a rule name", variant: "destructive" });
      return;
    }

    if (formData.effectiveRate < 0) {
      toast({ title: "Error", description: "Effective rate must be greater than or equal to 0", variant: "destructive" });
      return;
    }

    createRuleMutation.mutate({
      name: formData.name,
      retailerId: null, // Task determines retailer
      appliesToAllRetailers: true, // Since task determines retailer
      rateTemplateId: null, // Not using rate templates anymore
      taskId: formData.taskId,
      campaignCountMin: formData.campaignCountMin,
      campaignCountMax: formData.campaignCountMax || null,
      storeCountMin: formData.storeCountMin,
      storeCountMax: formData.storeCountMax || null,
      effectiveRate: formData.effectiveRate,
      isActive: formData.isActive,
      mediaFormatId: null, // Not using media formats anymore
      conditionLogic: formData.conditionLogic,
      createdBy: 1
    });
  };

  const handleEditRule = (rule: any) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name || "",
      taskId: rule.taskId,
      campaignCountMin: rule.campaignCountMin,
      campaignCountMax: rule.campaignCountMax,
      storeCountMin: rule.storeCountMin,
      storeCountMax: rule.storeCountMax,
      effectiveRate: rule.effectiveRate || 0,
      isActive: rule.isActive,
      conditionLogic: rule.conditionLogic || "AND"
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateRule = () => {
    if (!editingRule) return;

    updateRuleMutation.mutate({
      id: editingRule.id,
      updates: {
        name: formData.name || editingRule.name, // Use existing name if not modified
        retailerId: null, // Task determines retailer
        rateTemplateId: null, // Not using rate templates anymore
        taskId: formData.taskId,
        campaignCountMin: formData.campaignCountMin,
        campaignCountMax: formData.campaignCountMax || null,
        storeCountMin: formData.storeCountMin,
        storeCountMax: formData.storeCountMax || null,
        effectiveRate: formData.effectiveRate,
        isActive: formData.isActive,
        conditionLogic: formData.conditionLogic,
        createdBy: 1 // Required field for validation
      }
    });
  };



  const formatRange = (min: number, max?: number | null) => {
    if (max === null || max === undefined) {
      return `${min}+`;
    }
    return min === max ? `${min}` : `${min}-${max}`;
  };

  const formatRuleConditions = (rule: any) => {
    const conditions = [];
    
    // Add task condition - now always present and first
    if (rule.taskId) {
      const task = tasks.find(t => t.id === rule.taskId);
      conditions.push(`Task: ${task?.name || 'Unknown'}`);
    }
    
    // Add campaign count condition (campaigns per unique store)
    conditions.push(`Campaigns per Store: ${formatRange(rule.campaignCountMin, rule.campaignCountMax)}`);
    
    // Add store condition
    conditions.push(`Store Count: ${formatRange(rule.storeCountMin, rule.storeCountMax)}`);
    
    const logic = rule.conditionLogic || 'AND';
    return `IF ${conditions.join(` ${logic} `)} THEN €${(rule.effectiveRate || 0).toFixed(2)} per task`;
  };

  const refreshData = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/operator-discount-rules-enhanced"] });
  };

  // Simplified rule display - no complex grouping
  const enhancedRules = allRules.map(rule => ({ ...rule, type: 'enhanced' }));

  // Show loading state for critical data
  if (tasksLoading || rulesLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Task Rules Management</h1>
            <p className="text-muted-foreground mt-1">Loading task rules...</p>
          </div>
          <Button disabled>
            <Plus className="h-4 w-4 mr-2" />
            Create Enhanced Rule
          </Button>
        </div>
        <LoadingState variant="skeleton" rows={8} message="Loading task rules..." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Task Rules Management</h1>
          <p className="text-muted-foreground mt-1">
            Create task-specific payment rules based on campaigns per store (number of campaigns per unique store)
          </p>
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              Payment Formula: (Tasks Completed × Rate per Task) × Discount Rule
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Rules calculate operator payments based on the number of campaigns per unique store. The campaign count is counted per store, not per campaign.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsCreateDialogOpen(true)} disabled={tasksLoading}>
            <Plus className="h-4 w-4 mr-2" />
            Create Enhanced Rule
          </Button>
        </div>
      </div>

      {/* Legacy Rules Section - only show if data exists */}
      {legacyRules.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/30 dark:bg-orange-900/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-800 dark:text-orange-200">
              <FileText className="h-5 w-5" />
              Legacy Rules (Ready for Deletion)
            </CardTitle>
            <CardDescription className="text-orange-700 dark:text-orange-300">
              These are old rules that can be safely deleted. The new task-based rules above provide better functionality.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {legacyRules.map((rule) => (
                <div key={rule.id} className="p-3 border border-orange-200 rounded-lg bg-white dark:bg-orange-900/5 flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{rule.name}</span>
                      <Badge variant="outline" className="text-orange-700 border-orange-700">
                        Legacy
                      </Badge>
                      <Badge variant={rule.isActive ? "default" : "secondary"}>
                        {rule.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div className="font-mono text-xs bg-orange-100 dark:bg-orange-900/20 p-2 rounded mt-1">
                        IF campaigns: {rule.campaignCountMin}{rule.campaignCountMax ? `-${rule.campaignCountMax}` : '+'} AND stores: {rule.storeCountMin}{rule.storeCountMax ? `-${rule.storeCountMax}` : '+'} THEN discount: {(rule.discountPercent * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete the legacy rule "${rule.name}"?`)) {
                          deleteLegacyRuleMutation.mutate(rule.id);
                        }
                      }}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Enhanced Rules Section */}
      <Card>
        <CardHeader>
          <CardTitle>Task Rules</CardTitle>
          <CardDescription>
            {enhancedRules.length} rule{enhancedRules.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {enhancedRules.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No task rules configured yet. Click "Create Enhanced Rule" to get started.
              </p>
            ) : (
              enhancedRules.map((rule) => (
                <div key={rule.id} className="p-3 border rounded flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{rule.name}</span>
                      <Badge variant={rule.isActive ? "default" : "secondary"}>
                        {rule.isActive ? "Active" : "Inactive"}
                      </Badge>
                      {rule.appliesToAllRetailers && (
                        <Badge variant="outline" className="text-blue-600">
                          All Retailers
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div className="font-mono text-xs bg-muted p-2 rounded mt-1">
                        {formatRuleConditions(rule)}
                      </div>
                      {/* Effective Rate Display */}
                      {rule.taskId && (
                        <div className="text-xs text-green-700 dark:text-green-300 font-medium mt-2">
                          {(() => {
                            const task = tasks.find(t => t.id === rule.taskId);
                            if (!task) return null;
                            
                            const defaultRate = task.defaultRate || 0;
                            const effectiveRate = rule.effectiveRate || 0;
                            
                            return `💰 Effective Rate: €${effectiveRate.toFixed(2)} per task (default: €${defaultRate.toFixed(2)})`;
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEditRule(rule)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteRuleMutation.mutate(rule.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Create Rule Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Task Rule</DialogTitle>
            <DialogDescription>
              Create task-specific payment rules. The selected task determines which retailers this rule applies to. Rules calculate payments based on the number of campaigns per unique store (not stores per campaign).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="task">Task <span className="text-red-500">*</span></Label>
              <Select value={formData.taskId ? formData.taskId.toString() : undefined} onValueChange={(value) => setFormData({...formData, taskId: parseInt(value)})}>
                <SelectTrigger>
                  <SelectValue placeholder={tasksLoading ? "Loading tasks..." : "Select task"} />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {tasks.map((task) => (
                    <SelectItem key={task.id} value={task.id.toString()}>
                      {task.name} - {task.category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="name">Rule Name</Label>
              <Input
                id="name"
                value={formData.name ?? ""}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter rule name"
              />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> This rule will apply to the selected task regardless of retailer. 
                The task itself determines which retailers it applies to.
              </p>
            </div>



            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="taskMin">Min Campaigns per Store</Label>
                <Input
                  id="taskMin"
                  type="number"
                  value={formData.campaignCountMin || ""}
                  onChange={(e) => setFormData({...formData, campaignCountMin: e.target.value ? parseInt(e.target.value) : 1})}
                  min="1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum number of campaigns per unique store
                </p>
              </div>
              <div>
                <Label htmlFor="taskMax">Max Campaigns per Store</Label>
                <Input
                  id="taskMax"
                  type="number"
                  value={formData.campaignCountMax || ""}
                  onChange={(e) => setFormData({...formData, campaignCountMax: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="1"
                  placeholder="No limit"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of campaigns per unique store (leave empty for no limit)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="storeMin">Min Stores</Label>
                <Input
                  id="storeMin"
                  type="number"
                  value={formData.storeCountMin || ""}
                  onChange={(e) => setFormData({...formData, storeCountMin: e.target.value ? parseInt(e.target.value) : 1})}
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="storeMax">Max Stores</Label>
                <Input
                  id="storeMax"
                  type="number"
                  value={formData.storeCountMax || ""}
                  onChange={(e) => setFormData({...formData, storeCountMax: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="1"
                  placeholder="No limit"
                />
              </div>
            </div>

            {/* Task-Based Rule Logic */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Rule Logic</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Set the logic for when this rule applies to the selected task:
                "IF campaigns per store is Y AND store count is Z THEN rate is..."
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="conditionLogic">Condition Logic</Label>
                  <Select value={formData.conditionLogic} onValueChange={(value) => setFormData({...formData, conditionLogic: value as "AND" | "OR"})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="AND">AND (All conditions must be met)</SelectItem>
                      <SelectItem value="OR">OR (Any condition can be met)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="effectiveRate">Effective Rate per Task (€)</Label>
              <Input
                id="effectiveRate"
                type="number"
                step="0.01"
                value={formData.effectiveRate || ""}
                onChange={(e) => setFormData({...formData, effectiveRate: e.target.value ? parseFloat(e.target.value) : 0})}
                min="0"
                placeholder="e.g., 15.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the final rate operators will receive per task when this rule applies
              </p>
            </div>

            {/* Task Information */}
            {formData.taskId && formData.effectiveRate >= 0 && (
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Task Information</h4>
                <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  {(() => {
                    const selectedTask = tasks.find(t => t.id === formData.taskId);
                    if (!selectedTask) return null;
                    
                    const defaultRate = selectedTask.defaultRate || 0;
                    
                    return (
                      <>
                        <p><strong>Task:</strong> {selectedTask.name}</p>
                        <p><strong>Default Rate:</strong> €{defaultRate.toFixed(2)} per task</p>
                        <p><strong>Rule Effective Rate:</strong> €{formData.effectiveRate.toFixed(2)} per task</p>
                        <p className="text-xs italic mt-2">
                          This effective rate will be applied directly when conditions are met.
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                id="active"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
              />
              <Label htmlFor="active">Active</Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateRule} disabled={createRuleMutation.isPending}>
                {createRuleMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Rule Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task Rule</DialogTitle>
            <DialogDescription>
              Update the task-based payment rule. The selected task automatically determines which retailers this rule applies to. Rules calculate payments based on the number of campaigns per unique store (not stores per campaign).
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Rule Name</Label>
              <Input
                id="edit-name"
                value={formData.name || ""}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Enter rule name"
              />
            </div>

            <div>
              <Label htmlFor="edit-task">Task</Label>
              <Select value={formData.taskId ? formData.taskId.toString() : ""} onValueChange={(value) => setFormData({...formData, taskId: parseInt(value)})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select task" />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  {tasks.map((task) => (
                    <SelectItem key={task.id} value={task.id.toString()}>
                      {task.name} (€{task.defaultRate?.toFixed(2) || '0.00'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-campaignMin">Min Campaigns per Store</Label>
                <Input
                  id="edit-campaignMin"
                  type="number"
                  value={formData.campaignCountMin || ""}
                  onChange={(e) => setFormData({...formData, campaignCountMin: e.target.value ? parseInt(e.target.value) : 1})}
                  min="1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Minimum number of campaigns per unique store
                </p>
              </div>
              <div>
                <Label htmlFor="edit-campaignMax">Max Campaigns per Store</Label>
                <Input
                  id="edit-campaignMax"
                  type="number"
                  value={formData.campaignCountMax || ""}
                  onChange={(e) => setFormData({...formData, campaignCountMax: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="1"
                  placeholder="No limit"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum number of campaigns per unique store (leave empty for no limit)
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-storeMin">Min Store Count</Label>
                <Input
                  id="edit-storeMin"
                  type="number"
                  value={formData.storeCountMin || ""}
                  onChange={(e) => setFormData({...formData, storeCountMin: e.target.value ? parseInt(e.target.value) : 1})}
                  min="1"
                />
              </div>
              <div>
                <Label htmlFor="edit-storeMax">Max Store Count</Label>
                <Input
                  id="edit-storeMax"
                  type="number"
                  value={formData.storeCountMax || ""}
                  onChange={(e) => setFormData({...formData, storeCountMax: e.target.value ? parseInt(e.target.value) : undefined})}
                  min="1"
                  placeholder="No limit"
                />
              </div>
            </div>

            {/* Task-Based Rule Logic */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Rule Logic</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Set the logic for when this rule applies to the selected task:
                "IF campaigns per store is Y AND store count is Z THEN rate is..."
              </p>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-conditionLogic">Condition Logic</Label>
                  <Select value={formData.conditionLogic} onValueChange={(value) => setFormData({...formData, conditionLogic: value as "AND" | "OR"})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="AND">AND (All conditions must be met)</SelectItem>
                      <SelectItem value="OR">OR (Any condition can be met)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="edit-effectiveRate">Effective Rate per Task (€)</Label>
              <Input
                id="edit-effectiveRate"
                type="number"
                step="0.01"
                value={formData.effectiveRate || ""}
                onChange={(e) => setFormData({...formData, effectiveRate: e.target.value ? parseFloat(e.target.value) : 0})}
                min="0"
                placeholder="e.g., 15.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter the final rate operators will receive per task when this rule applies
              </p>
            </div>

            {/* Task Information */}
            {formData.taskId && formData.effectiveRate >= 0 && (
              <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-900/20">
                <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Task Information</h4>
                <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  {(() => {
                    const selectedTask = tasks.find(t => t.id === formData.taskId);
                    if (!selectedTask) return null;
                    
                    const defaultRate = selectedTask.defaultRate || 0;
                    
                    return (
                      <>
                        <p><strong>Task:</strong> {selectedTask.name}</p>
                        <p><strong>Default Rate:</strong> €{defaultRate.toFixed(2)} per task</p>
                        <p><strong>Rule Effective Rate:</strong> €{formData.effectiveRate.toFixed(2)} per task</p>
                        <p className="text-xs italic mt-2">
                          This effective rate will be applied directly when conditions are met.
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-2">
              <Switch
                id="edit-active"
                checked={formData.isActive}
                onCheckedChange={(checked) => setFormData({...formData, isActive: checked})}
              />
              <Label htmlFor="edit-active">Active</Label>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRule} disabled={updateRuleMutation.isPending}>
                {updateRuleMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ComprehensiveRemuneration() {
  return (
    <ErrorBoundary>
      <ComprehensiveRemunerationContent />
    </ErrorBoundary>
  );
}
